import { Box } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'

import { observer } from 'mobx-react'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import FiltersForm from 'modules/core/lib/forms/Filters'
import useLocalStorage from 'modules/core/lib/useLocalStorage'
import users from 'modules/core/store/collections/users'

import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import orderSupplements from 'modules/payroll_bundle/components/common/helper/orderItems'
import type { SupplementMatchDataType } from 'modules/payroll_bundle/components/common/helper/worksheet'
import {
  ImportData,
  useBulkImportSupplements,
} from 'modules/payroll_bundle/lib/bulkImportSupplementsContext'
import { UPDATE_DATA } from 'modules/payroll_bundle/lib/constants'
import { isValid } from 'modules/payroll_bundle/pages/SupplementsPage/BulkImport/_components/_Table/RowValidations'
import Table from 'modules/payroll_bundle/pages/SupplementsPage/BulkImport/_components/_Table/index'
import { Supplements } from 'modules/payroll_bundle/store/collections/supplements'
import type Supplement from 'modules/payroll_bundle/store/models/Supplement'

import AssignSupplementTypeModal from '../_components/AssignSupplementTypeModal'
import useMatchEmployeeData from './useMatchEmployeeData'

interface Props {
  filteredEmployees: Employee[]
  matchedData: SupplementMatchDataType[]
}

const Matcher = ({ matchedData, filteredEmployees }: Props) => {
  const {
    supplementDispatch,
    selectedSupplements,
    importedSupplements,
    supplementTypes,
    showAssignTypeModal,
    editingSupplementType,
  } = useBulkImportSupplements()

  const [filterByFromStorage] = useLocalStorage<string>(
    LOCAL_STORAGE_KEYS.bulkImportSupplementFilter
  )
  const filtersForm = useLazyRef(
    () =>
      new FiltersForm({
        sortAttribute: 'full_name',
        sortDirection: 'asc',
        filterBy: filterByFromStorage,
      })
  ).current

  // Tries to match the person names in `matchedData` with the corresponding employee ids
  const matches = useMatchEmployeeData(matchedData, filteredEmployees)

  useUnsafeEffect(() => {
    supplementDispatch({
      type: UPDATE_DATA,
      payload: { importedSupplements: matches as ImportData[] },
    })
  }, [matches, filteredEmployees.length])

  const supplementItems = new Supplements(importedSupplements)
  const supplements: Supplement[] = orderSupplements(supplementItems.models, filtersForm)

  const canBeSelected = (): number[] => {
    const selectables = supplements.filter((supplement) => isValid(supplement, supplementTypes))

    return selectables.map((e) => e.id)
  }

  const onSelectAll = () => {
    selectedSupplements.size > 0 ? setSelectedSupplements([]) : selectAll()
  }

  const selectAll = () => {
    const ids = canBeSelected()
    setSelectedSupplements(ids)
  }

  const setSelectedSupplements = (value: number[]) => {
    supplementDispatch({
      type: UPDATE_DATA,
      payload: {
        selectedSupplements: new Set(value),
      },
    })
  }

  const onSelectItem = (supplement: Supplement) => {
    const id = supplement.get('id')
    selectedSupplements.has(id) ? onUnselect(id) : onSelect(id)
  }

  const onSelect = (supplementId: number) => {
    selectedSupplements.add(supplementId)
    setSelectedSupplements([...selectedSupplements])
  }

  const onUnselect = (supplementId: number) => {
    selectedSupplements.delete(supplementId)
    setSelectedSupplements([...selectedSupplements])
  }

  const getLegalEntity = (userId: number) => {
    const employee = users.get(userId)?.employee
    const legalEntity = employee && employeeLegalEntity(employee)

    return { id: legalEntity?.id, name: legalEntity?.name }
  }

  // Augments the context's `importedSupplements` item specified by `id` with
  // the data of the employee specified by `employeeId`
  // (employee_id, userId, legal_entity_id, legal_entity_name)
  const onAssignEmployee = async (employeeId: number, id: number) => {
    const updated = supplements.map((item) => item.toJS())
    const assigned = updated.find((item) => item.id === id)
    const employee = employees.get(employeeId)
    const userId = employee && employee.attributes.access_id

    if (!assigned) return

    if (userId) onUnselect(assigned.userId)

    assigned.userId = userId
    assigned.employee_id = employeeId

    const userLegalEntity = getLegalEntity(userId!)
    assigned.legal_entity_id = userLegalEntity.id
    assigned.legal_entity_name = userLegalEntity.name

    supplementDispatch({
      type: UPDATE_DATA,
      payload: {
        importedSupplements: updated as ImportData[],
      },
    })
  }

  return (
    <>
      <Box flexDirection="column" alignItems="center">
        <Table
          checkable
          filtersForm={filtersForm}
          onSelectAll={onSelectAll}
          selected={selectedSupplements}
          selectable={canBeSelected()}
          supplements={supplements}
          supplementTypes={supplementTypes}
          onSelectItem={onSelectItem}
          onAssignEmployee={onAssignEmployee}
          supplementDispatch={supplementDispatch}
        />
      </Box>
      {showAssignTypeModal && <AssignSupplementTypeModal supplementType={editingSupplementType} />}
    </>
  )
}

export default observer(Matcher)
